
import { Component, DestroyRef, HostListener, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { PdfSchematicViewerComponent } from './components/pdf-schematic-viewer/pdf-schematic-viewer.component';
import { PdfAnnotationStateService } from './services/pdf-annotation-state.service';
import { FmedaPdfWindowSnapshot, FmedaPdfWindowSyncService } from './services/fmeda-pdf-window-sync.service';

@Component({
    selector: 'app-fmeda-pdf-popout',
    standalone: true,
    imports: [PdfSchematicViewerComponent],
    templateUrl: './fmeda-pdf-popout.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './fmeda-pdf-popout.component.css'
})
export class FmedaPdfPopoutComponent {
    private readonly destroyRef = inject(DestroyRef);
    private readonly pdfAnnotationStateService = inject(PdfAnnotationStateService);
    private readonly route = inject(ActivatedRoute);
    private readonly syncService = inject(FmedaPdfWindowSyncService);
    private readonly presenceHeartbeatMs = 2000;
    private readonly presenceIntervalId: number;

    readonly pdfUrl = signal('');
    readonly focusedDesignator = signal('');

    constructor() {
        this.syncService.setPopoutOpen(true);
        this.applySnapshot(this.syncService.getSnapshot());

        this.route.queryParamMap
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(params => {
                const routePdfUrl = params.get('pdfUrl')?.trim() ?? '';
                const routeDesignator = params.get('designator')?.trim() ?? '';

                if (routePdfUrl && !this.pdfUrl()) {
                    this.pdfUrl.set(routePdfUrl);
                }

                if (routeDesignator && !this.focusedDesignator()) {
                    this.focusedDesignator.set(routeDesignator);
                    this.pdfAnnotationStateService.highlightComponentInPdf(routeDesignator);
                }
            });

        this.syncService.snapshot$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(snapshot => {
                this.applySnapshot(snapshot);
            });

        this.syncService.focusFromRemote$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(designator => {
                this.focusedDesignator.set(designator);
                this.pdfAnnotationStateService.highlightComponentInPdf(designator);
            });

        this.pdfAnnotationStateService.componentToSelect$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(designator => {
                if (!designator) {
                    return;
                }

                this.focusedDesignator.set(designator);
                this.syncService.publishFocus(designator);
            });

        this.presenceIntervalId = window.setInterval(() => {
            this.syncService.setPopoutOpen(true);
        }, this.presenceHeartbeatMs);

        this.destroyRef.onDestroy(() => {
            window.clearInterval(this.presenceIntervalId);
            this.syncService.setPopoutOpen(false);
        });
    }

    @HostListener('window:beforeunload')
    onBeforeUnload(): void {
        this.syncService.setPopoutOpen(false);
    }

    private applySnapshot(snapshot: FmedaPdfWindowSnapshot): void {
        if (snapshot.pdfUrl) {
            this.pdfUrl.set(snapshot.pdfUrl);
        }

        if (snapshot.focusedDesignator) {
            this.focusedDesignator.set(snapshot.focusedDesignator);
        }

        this.pdfAnnotationStateService.loadAnnotations(snapshot.annotations);

        if (snapshot.focusedDesignator) {
            this.pdfAnnotationStateService.highlightComponentInPdf(snapshot.focusedDesignator);
        }
    }
}